/**
 * カート通知制御システム
 * ファイル名: cart-notification-controller.js
 * 配置場所: assets/cart-notification-controller.js
 * 作成者: yama
 * カート通知ポップアップでの予約商品制御
 */

class CartNotificationController {
  constructor() {
    this.productTagsCache = new Map(); // 商品タグのキャッシュ（メインコントローラーと共有可能）
    this.init();
  }

  /**
   * 初期化
   */
  init() {
    // カート通知の表示を監視
    this.observeCartNotification();
    
    // カート追加イベントを監視
    this.setupCartAddListeners();
  }

  /**
   * Product APIで商品のタグを取得
   */
  async fetchProductTags(productHandle) {
    // メインコントローラーのキャッシュがあれば使用
    if (window.presaleController && window.presaleController.productTagsCache) {
      if (window.presaleController.productTagsCache.has(productHandle)) {
        return window.presaleController.productTagsCache.get(productHandle);
      }
    }

    // 自身のキャッシュをチェック
    if (this.productTagsCache.has(productHandle)) {
      return this.productTagsCache.get(productHandle);
    }

    try {
      const response = await fetch(`/products/${productHandle}.js`);
      const productData = await response.json();
      const tags = productData.tags || [];
      
      // 両方のキャッシュに保存
      this.productTagsCache.set(productHandle, tags);
      if (window.presaleController && window.presaleController.productTagsCache) {
        window.presaleController.productTagsCache.set(productHandle, tags);
      }
      
      return tags;
    } catch (error) {
      console.error(`商品タグ取得エラー (${productHandle}):`, error);
      return [];
    }
  }

  /**
   * 商品のタグをチェックして種別を判定
   */
  getProductType(tags) {
    if (!tags || !Array.isArray(tags)) return 'normal';
    
    if (tags.includes('受注期間終了')) return 'order_ended';
    if (tags.includes('#予約2')) return 'presale2';
    if (tags.includes('#予約')) return 'presale';
    return 'normal';
  }

  /**
   * カート内容を分析
   */
  async analyzeCart() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      
      if (!cartData || !cartData.items) {
        return { hasOrderEnded: false, isMixed: false, isEmpty: true };
      }

      let presaleCount = 0;
      let presale2Count = 0;
      let normalCount = 0;
      let orderEndedCount = 0;

      for (const item of cartData.items) {
        const tags = await this.fetchProductTags(item.handle);
        const productType = this.getProductType(tags);
        
        switch (productType) {
          case 'order_ended':
            orderEndedCount++;
            break;
          case 'presale':
            presaleCount++;
            break;
          case 'presale2':
            presale2Count++;
            break;
          case 'normal':
            normalCount++;
            break;
        }
      }

      // 混在チェック
      const activeTypeCount = [presaleCount > 0, presale2Count > 0, normalCount > 0].filter(Boolean).length;
      const isMixed = activeTypeCount > 1;
      const hasOrderEnded = orderEndedCount > 0;

      return {
        hasOrderEnded,
        isMixed,
        isEmpty: cartData.items.length === 0
      };
    } catch (error) {
      console.error('カート分析エラー:', error);
      return { hasOrderEnded: false, isMixed: false, isEmpty: true };
    }
  }

  /**
   * カート通知のUI制御
   */
  async updateCartNotificationUI() {
    const checkoutButton = document.querySelector('#cart-notification-form button');
    const orderEndedError = document.querySelector('#cart-notification-order-ended-error');
    const presaleError = document.querySelector('#cart-notification-presale-error');

    if (!checkoutButton) return;

    // 分析実行
    const analysis = await this.analyzeCart();

    if (analysis.isEmpty) {
      checkoutButton.setAttribute('disabled', 'disabled');
      this.hideErrorMessage(orderEndedError);
      this.hideErrorMessage(presaleError);
      return;
    }

    // エラー条件をチェック
    const hasError = analysis.hasOrderEnded || analysis.isMixed;

    if (hasError) {
      // エラーがある場合
      checkoutButton.setAttribute('disabled', 'disabled');
      checkoutButton.setAttribute('type', 'button');
      checkoutButton.removeAttribute('name');
      
      if (analysis.hasOrderEnded) {
        this.showErrorMessage(orderEndedError);
      } else {
        this.hideErrorMessage(orderEndedError);
      }
      
      if (analysis.isMixed) {
        this.showErrorMessage(presaleError);
      } else {
        this.hideErrorMessage(presaleError);
      }
    } else {
      // エラーがない場合
      checkoutButton.removeAttribute('disabled');
      checkoutButton.setAttribute('type', 'submit');
      checkoutButton.setAttribute('name', 'checkout');
      this.hideErrorMessage(orderEndedError);
      this.hideErrorMessage(presaleError);
    }
  }

  /**
   * エラーメッセージ表示
   */
  showErrorMessage(container) {
    if (container) {
      container.style.display = 'block';
    }
  }

  /**
   * エラーメッセージ非表示
   */
  hideErrorMessage(container) {
    if (container) {
      container.style.display = 'none';
    }
  }

  /**
   * カート通知の表示監視（MutationObserver）
   */
  observeCartNotification() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' || mutation.type === 'childList') {
          const cartNotification = document.querySelector('#cart-notification');
          if (cartNotification && this.isVisible(cartNotification)) {
            // カート通知が表示されたら制御を実行
            setTimeout(() => {
              this.updateCartNotificationUI();
            }, 100); // 少し遅延させてDOMの更新を待つ
          }
        }
      });
    });

    // body全体を監視
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });
  }

  /**
   * 要素が表示されているかチェック
   */
  isVisible(element) {
    if (!element) return false;
    
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && 
           style.visibility !== 'hidden' && 
           style.opacity !== '0' &&
           element.offsetWidth > 0 && 
           element.offsetHeight > 0;
  }

  /**
   * カート追加イベントのリスナー設定
   */
  setupCartAddListeners() {
    // フォーム送信イベント（一般的なカート追加）
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form.querySelector('[name="add"]')) {
        // カート追加フォームの送信後、通知表示を待つ
        setTimeout(() => {
          this.updateCartNotificationUI();
        }, 1000);
      }
    });

    // Ajax完了イベント（Ajax カート追加）
    document.addEventListener('ajaxComplete', () => {
      setTimeout(() => {
        this.updateCartNotificationUI();
      }, 500);
    });

    // カスタムイベント（テーマ固有）
    document.addEventListener('cart:item-added', () => {
      setTimeout(() => {
        this.updateCartNotificationUI();
      }, 500);
    });
  }
}

// グローバルインスタンスを作成
window.cartNotificationController = new CartNotificationController();